import { getCourseResources } from 'core/crud/courses.crud';
import { useOrganization } from 'core/hooks/useOrganization';
import useSystemSettings from 'core/hooks/useSystemSettings';
import { useUser } from 'core/lite/hooks/useUser';
import { getCourseItemsService } from 'core/services/courseItems.service';
import * as coreEntities from 'core/store/index';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { getCustomizedBy, getGuidCourseProgram } from 'core/utils/course';
import { decodeHtmlWithWhiteList } from 'core/utils/strings';
import { isDisregardedProgram } from 'core/utils/user';
import { CLIENT, CONTENT_TYPES, LESSON_TYPES_IDS } from 'core/variables/constant';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// Referencia estable para los selectores vacíos: evita recalcular los memos en cada render
// mientras las peticiones de cursos/programas no han resuelto.
const NO_ITEMS = [];

/**
 * Aplana los grupos de las secciones de tipo test en opciones de select (Edelvives).
 * @param {Array} sections - Secciones de recursos del curso
 * @returns {Array} Opciones de grupo de evaluación
 */
function parseTestGroups(sections = []) {
  return sections
    .filter((section) => section.type === 'test')
    .flatMap((section) =>
      (section.items ?? []).map((group) => ({
        value: `${section.guid}|${group.guid}`,
        label: group.name || section.name,
        sectionGuid: section.guid,
        groupGuid: group.guid,
        data: { section, group },
      }))
    );
}

/**
 * Custom hook for managing quiz location selection dialog state and logic
 * @param {Object} params
 * @param {boolean} params.isOpen - Whether the dialog is open
 * @param {Function} params.onClose - Closes the dialog
 * @param {Function} [params.onNavigate] - Called right before navigating to the quiz creation page
 * @returns {Object} State and handlers for the quiz location dialog
 */
function useQuizLocationDialog({ isOpen, onClose, onNavigate }) {
  const { t } = useTranslation(['chatbot', 'actions']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { client, tenantConfig } = useOrganization();
  const { enableOverlays } = useSystemSettings();
  const { schoolGuid, user } = useUser();

  // Detect Edelvives tenant - uses different flow (toolbox/resources page)
  const isEdelvives = client === CLIENT.EDELVIVES || window.location.hostname.includes('edv.tenant-ai-2.oneclicklabs.es');

  // Los tenants con integraciones de terceros ocultan los programas sin clase en el resto
  // del producto (useOrganization); el selector de ubicación respeta esa misma decisión.
  const shouldShowPrograms = tenantConfig?.showDisregardedPrograms ?? true;

  // Selection state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // Data state
  const [units, setUnits] = useState([]);

  // Edelvives: Test sections for deep link
  const [testSections, setTestSections] = useState([]);
  const [selectedTestGroup, setSelectedTestGroup] = useState(null);
  const [hasTestSection, setHasTestSection] = useState(false);

  // Loading state
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isLoadingTestSections, setIsLoadingTestSections] = useState(false);
  const [hasLoadedUnits, setHasLoadedUnits] = useState(false);
  const [hasLoadedTestSections, setHasLoadedTestSections] = useState(false);

  // Las clases (`front/courses`) son programa x grupo de clase: un profesor con licencia de
  // programa y sin grupo asignado no tiene ninguna, así que hay que ofrecerle también sus
  // programas (`front/programs`) como ubicación posible.
  const classes = useSelector((state) => coreEntities.courses.selectors.getCourses(state)) ?? NO_ITEMS;
  // El selector de programas reconstruye su resultado en cada llamada: con la lista vacía
  // devolvería un array nuevo por render, así que se colapsa en la referencia estable.
  const programs = useSelector((state) => {
    const items = coreEntities.programs.selectors.getPrograms(state)?.programs;
    return items?.length ? items : NO_ITEMS;
  });

  // Load courses and programs on mount
  useEffect(() => {
    dispatch(coreEntities.courses.actions.getCourses({ params: { offset: 0, pageSize: 1000 } }));
    dispatch(coreEntities.programs.actions.getPrograms({ isBook: 1, orderBy: 't.name + asc ' }));
  }, [dispatch]);

  // Ubicaciones disponibles: clases + programas licenciados que no tienen ninguna clase.
  // El dedupe se hace por guid de programa (una clase ya expone su programa).
  const locationOptions = useMemo(() => {
    const classOptions = classes.map((item) => ({
      value: item.guid,
      label: item.name || item.title,
      data: item,
      isProgram: false,
    }));

    if (!shouldShowPrograms) return classOptions;

    // El guid de programa de una clase depende de su tipo (getGuidCourseProgram): se dedupe
    // contra los dos para no ofrecer dos ubicaciones con el mismo `value` en el mismo select.
    const knownProgramGuids = new Set(classes.flatMap((item) => [item.program_guid, getGuidCourseProgram(item)].filter(Boolean)));
    const programOptions = [];

    programs.forEach((item) => {
      const programGuid = item.program?.guid ?? item.guid;
      if (!programGuid || knownProgramGuids.has(programGuid)) return;
      knownProgramGuids.add(programGuid);
      programOptions.push({
        value: programGuid,
        label: item.program?.name ?? item.name,
        // El resto del flujo (unidades, recursos, navegación) trabaja con el guid del
        // programa, igual que hace parsePrograms en core/lite/parsers.
        data: { ...item, guid: programGuid },
        isProgram: true,
      });
    });

    return [...classOptions, ...programOptions];
  }, [classes, programs, shouldShowPrograms]);

  // Opciones agrupadas solo cuando conviven clases y programas: con un único tipo de
  // ubicación las cabeceras de sección no aportan nada.
  const courseOptions = useMemo(() => {
    const classOptions = locationOptions.filter((option) => !option.isProgram);
    const programOptions = locationOptions.filter((option) => option.isProgram);

    if (classOptions.length === 0 || programOptions.length === 0) return locationOptions;

    return [
      { label: t('chatbot:quiz_location_group_classes'), options: classOptions },
      { label: t('chatbot:quiz_location_group_programs'), options: programOptions },
    ];
  }, [locationOptions, t]);

  // Derived lessons from selected unit (filter out MINT - Edelvives uses different flow)
  const lessons = useMemo(() => {
    const unitLessons = selectedUnit?.data?.items ?? NO_ITEMS;
    return unitLessons.filter((lesson) => lesson.lesson_type !== LESSON_TYPES_IDS.MINT);
  }, [selectedUnit]);

  // Load units when a course/program is selected
  useEffect(() => {
    if (!selectedCourse || isEdelvives) return undefined;

    // enableOverlays decide el endpoint (overlays vs base): esperar a que los system settings
    // resuelvan para no cargar la versión equivocada del programa.
    if (enableOverlays === undefined) return undefined;

    // Evita escribir estado obsoleto si se cambia de ubicación antes de que resuelva
    let isCancelled = false;

    async function loadCourseUnits() {
      setIsLoadingUnits(true);
      setUnits([]);

      try {
        const course = selectedCourse.data ?? {};
        // Los items de `front/programs` no traen `users`; getCustomizedBy cae entonces en el
        // guid del usuario actual, que es justo lo que necesita el endpoint de overlays.
        const customizedBy = getCustomizedBy(course, user) ?? [];
        // Un programa licenciado sin clase necesita `preview: 1`: sin él el backend responde
        // 403 y el interceptor global de axios desloguea al profesor.
        const isDisregarded = selectedCourse.isProgram || isDisregardedProgram(course);
        const response = await getCourseItemsService(selectedCourse.value, schoolGuid, customizedBy, enableOverlays, isDisregarded);

        if (isCancelled) return;
        setUnits(Array.isArray(response) ? response : (response?.items ?? []));
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading units:', error);
        setUnits([]);
      } finally {
        // El loading se apaga siempre: si la petición se cancela al cerrar el diálogo, dejarlo
        // en true mostraría un spinner permanente en la siguiente apertura.
        setIsLoadingUnits(false);
        if (!isCancelled) setHasLoadedUnits(true);
      }
    }

    loadCourseUnits();

    return () => {
      isCancelled = true;
    };
    // `user` solo se usa para resolver su guid: se depende del guid y no de la referencia
    // del objeto para que un selector que devuelva una copia no relance la petición.
  }, [selectedCourse, isEdelvives, enableOverlays, schoolGuid, user?.guid]);

  // Load test sections for the Edelvives deep link
  useEffect(() => {
    if (!isEdelvives || !selectedCourse) return undefined;

    let isCancelled = false;

    async function loadTestSections() {
      setIsLoadingTestSections(true);
      setTestSections([]);
      setSelectedTestGroup(null);
      setHasTestSection(false);

      try {
        const course = selectedCourse.data ?? {};
        const courseGuid = getGuidCourseProgram(course) ?? course.guid;
        const referencedCourseGuid = course.type === COURSE_TYPES.REFERENCED ? course.guid : null;
        // Mismo motivo que en las unidades: sin `preview` un programa sin clase responde 403
        // y el interceptor global desloguea al profesor.
        const isDisregarded = selectedCourse.isProgram || isDisregardedProgram(course);
        const params = isDisregarded ? { preview: 1 } : undefined;

        const response = await getCourseResources({ courseGuid, referencedCourseGuid, params });
        if (isCancelled) return;

        const sections = response?.data?.data?.items ?? [];

        // Check if any test section exists
        setHasTestSection(sections.some((section) => section.type === 'test'));

        const testGroups = parseTestGroups(sections);
        setTestSections(testGroups);

        // Auto-select if only one group
        if (testGroups.length === 1) {
          setSelectedTestGroup(testGroups[0]);
        }
      } catch (error) {
        if (isCancelled) return;
        console.error('[QuizLocationDialog] Error loading test sections:', error);
        setTestSections([]);
      } finally {
        // Mismo motivo que en las unidades: el loading no puede quedarse encendido si la
        // petición se cancela al cerrar el diálogo.
        setIsLoadingTestSections(false);
        if (!isCancelled) setHasLoadedTestSections(true);
      }
    }

    loadTestSections();

    return () => {
      isCancelled = true;
    };
  }, [isEdelvives, selectedCourse]);

  // Transform units to select options
  const unitOptions = useMemo(() => {
    if (!selectedCourse || units.length === 0) return NO_ITEMS;

    return units.map((unit, index) => {
      const section = unit.section;
      const sectionGuid = (typeof section === 'object' ? section?.guid : unit.guid) || unit.guid;
      const sectionName = typeof section === 'object' ? section?.name : section;

      return {
        value: sectionGuid,
        label: decodeHtmlWithWhiteList(sectionName || `${t('chatbot:Unit')} ${index + 1}`),
        data: unit,
      };
    });
  }, [selectedCourse, units, t]);

  // Transform lessons to select options
  const lessonOptions = useMemo(() => {
    if (!selectedUnit || lessons.length === 0) return NO_ITEMS;

    return lessons.map((lesson, index) => ({
      value: lesson.guid,
      label: decodeHtmlWithWhiteList(lesson.lesson_name || `${t('chatbot:Lesson')} ${index + 1}`),
      data: lesson,
    }));
  }, [selectedUnit, lessons, t]);

  // Handlers
  const resetSelections = useCallback(() => {
    setSelectedCourse(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setUnits([]);
    setTestSections([]);
    setSelectedTestGroup(null);
    setHasTestSection(false);
    setHasLoadedUnits(false);
    setHasLoadedTestSections(false);
  }, []);

  // react-select dispara onChange también sobre la opción ya seleccionada, y con la misma
  // referencia de objeto: sin el guard de identidad se limpiaría el estado sin que el efecto
  // de carga (dep `selectedCourse`) se relanzase, dejando el select de unidades vacío.
  const handleCourseChange = useCallback(
    (option) => {
      if (!option || option.value === selectedCourse?.value) return;
      resetSelections();
      setSelectedCourse(option);
    },
    [resetSelections, selectedCourse]
  );

  const handleTestGroupChange = useCallback((option) => {
    setSelectedTestGroup(option);
  }, []);

  const handleUnitChange = useCallback((option) => {
    setSelectedUnit(option);
    setSelectedLesson(null);
  }, []);

  const handleLessonChange = useCallback((option) => {
    setSelectedLesson(option);
  }, []);

  // Reset selections when the dialog closes
  useEffect(() => {
    if (!isOpen) resetSelections();
  }, [isOpen, resetSelections]);

  // Build navigation URL to quiz creation page
  const navigationUrl = useMemo(() => {
    if (!selectedCourse) return null;

    const courseGuid = selectedCourse.value;
    // Un programa licenciado sin clase solo es navegable en modo preview: sin `preview=1` las
    // vistas de curso piden el detalle como si hubiera clase y el backend responde 403.
    const isProgramLocation = Boolean(selectedCourse.isProgram);

    function buildUrl(path, queryParams = []) {
      const params = isProgramLocation ? [...queryParams, 'preview=1'] : queryParams;
      return params.length > 0 ? `${path}?${params.join('&')}` : path;
    }

    // Edelvives: Deep link to create-evaluations page
    if (isEdelvives) {
      // No test sections found - return null (error will be shown)
      if (!selectedTestGroup) return null;
      return buildUrl(`/course/${courseGuid}/create-evaluations/ia/${selectedTestGroup.sectionGuid}/${selectedTestGroup.groupGuid}`);
    }

    if (!selectedUnit) return null;

    if (lessons.length === 0) {
      // El `value` de la opción es el guid del item de curso; para navegar hace falta el de la
      // lección (`lesson_guid`), que es lo que resuelve la vista de creación.
      const unitLessonGuid = selectedUnit.data?.lesson_guid || selectedUnit.value;
      return buildUrl(`/course/${courseGuid}/lesson/${unitLessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
    }

    if (!selectedLesson) return null;

    const lessonGuid = selectedLesson.data?.lesson_guid || selectedLesson.value;
    const lessonType = selectedLesson.data?.lesson_type;
    const isPowerCourse = selectedCourse.data?.has_power_lesson === 1;

    // For power lessons, navigate to lesson page with addQuiz param
    if (lessonType === LESSON_TYPES_IDS.POWER || isPowerCourse) {
      return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}`, ['addQuiz=true', 'enableAI=true']);
    }

    // For regular lessons, navigate to quiz creation page
    return buildUrl(`/course/${courseGuid}/lesson/${lessonGuid}/add/${CONTENT_TYPES.TEST}`, ['enableAI=true']);
  }, [selectedCourse, selectedUnit, selectedLesson, selectedTestGroup, isEdelvives, lessons]);

  // Edelvives: Can navigate if course selected AND test section available
  // Other tenants: Need course and lesson, OR course and unit for modules-only programs
  const canNavigate = useMemo(() => {
    if (isEdelvives) {
      if (!selectedCourse) return false;
      if (isLoadingTestSections) return false;
      // Require at least one test section to navigate
      if (testSections.length === 0) return false;
      // If there are multiple test sections, require selection
      if (testSections.length > 1) return Boolean(selectedTestGroup);
      // If only one test section (auto-selected), can navigate
      return true;
    }

    if (lessons.length === 0) {
      return Boolean(selectedCourse && selectedUnit);
    }

    return Boolean(selectedCourse && selectedLesson);
  }, [isEdelvives, selectedCourse, selectedUnit, selectedLesson, lessons, testSections, selectedTestGroup, isLoadingTestSections]);

  const handleConfirm = useCallback(() => {
    if (navigationUrl) {
      history.push(navigationUrl);
    }
    if (onNavigate) onNavigate();
    onClose();
  }, [history, navigationUrl, onClose, onNavigate]);

  const hasLessons = lessons.length > 0;
  const hasUnitsError = hasLoadedUnits && !isLoadingUnits && unitOptions.length === 0;
  // Only show lessons error when the unit has lessons (not for modules-only programs)
  const hasLessonsError = Boolean(selectedUnit) && !isLoadingUnits && lessonOptions.length === 0 && hasLessons;
  const hasContentError = !isEdelvives && (hasUnitsError || hasLessonsError);
  const isUnitSelectDisabled = unitOptions.length === 0 && !isLoadingUnits;
  const isLessonSelectDisabled = lessonOptions.length === 0;

  // Edelvives: Differentiate between "no test section at all" vs "test section exists but has no groups"
  const hasTestSectionsLoaded = isEdelvives && hasLoadedTestSections && !isLoadingTestSections;
  const hasNoTestSection = hasTestSectionsLoaded && !hasTestSection;
  const hasNoGroups = hasTestSectionsLoaded && hasTestSection && testSections.length === 0;
  const hasMultipleTestSections = isEdelvives && testSections.length > 1;

  // SelectCustomizable pinta el contenedor accesible del error: solo espera el texto.
  const unitError = hasUnitsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;
  const lessonError = hasLessonsError ? t('chatbot:teacher_tools_error_content_unavailable_title') : undefined;

  return {
    // Options
    courseOptions,
    unitOptions,
    lessonOptions,
    testSections,

    // Selected values
    selectedCourse,
    selectedUnit,
    selectedLesson,
    selectedTestGroup,

    // Loading states
    isLoadingUnits,
    isLoadingTestSections,

    // Handlers
    handleCourseChange,
    handleUnitChange,
    handleLessonChange,
    handleTestGroupChange,
    handleConfirm,

    // Computed
    canNavigate,
    hasContentError,
    hasLessons,
    hasLessonsError,
    hasMultipleTestSections,
    hasNoGroups,
    hasNoTestSection,
    hasUnitsError,
    isLessonSelectDisabled,
    isUnitSelectDisabled,
    lessonError,
    unitError,

    // Expuestos solo para los tests: el componente no los consume.
    navigationUrl,
    resetSelections,

    // Tenant flags
    isEdelvives,

    t,
  };
}

export default useQuizLocationDialog;
